import React, { useRef, type ReactNode } from "react";
import { Box } from "@mui/material";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform
} from "framer-motion";

type WelcomeParallaxSectionProps = {
  children: ReactNode;
  /** Alternating index — even sections drift up, odd sections drift down while scrolling. */
  index?: number;
  /** Peak vertical shift in pixels while the section crosses the viewport. */
  offset?: number;
};

/** Scroll-linked parallax for whole welcome sections (About, Features, FAQ, etc.). */
const WelcomeParallaxSection: React.FC<WelcomeParallaxSectionProps> = ({
  children,
  index = 0,
  offset = 80
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const drift = index % 2 === 0 ? offset : -offset;
  const y = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [drift * 0.7, 0, -drift * 0.7]
  );

  return (
    <Box ref={ref} sx={{ width: "100%", overflow: "visible" }}>
      <motion.div
        style={{
          y: prefersReducedMotion ? 0 : y,
          position: "relative",
          willChange: prefersReducedMotion ? undefined : "transform"
        }}
      >
        {children}
      </motion.div>
    </Box>
  );
};

export default WelcomeParallaxSection;
